import React, { useState, useEffect, useMemo } from "react";
import { 
  Card, 
  Checkbox, 
  Button, 
  Space, 
  message, 
  Typography, 
  Divider, 
  Badge, 
  Tooltip, 
  Input, 
  Alert,
  Empty,
  Tag,
  Tabs
} from "antd";
import { 
  EyeOutlined, 
  EyeInvisibleOutlined, 
  ReloadOutlined,
  SettingOutlined,
  SearchOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DragOutlined,
  UnorderedListOutlined
} from "@ant-design/icons";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { items } from "../../constants/items.constant";
import { useAuth } from "../../contexts/auth.context";
import { useSidebarVisibility } from "../../hooks/useSidebarVisibility";
import { UserRole } from "../../types/user.type";

const { Title, Text, Paragraph } = Typography;

interface MenuItemWithVisibility {
  key: string;
  label: string;
  visible: boolean;
  hasChildren: boolean;
  icon?: React.ReactElement;
  children?: MenuItemWithVisibility[];
}

export default function SidebarSettings() {
  const { authState } = useAuth();
  const { 
    isItemVisible, 
    setItemVisibility, 
    setItemsVisibility, 
    resetVisibility, 
    getAllSettings,
    getItemOrder,
    setItemOrder,
    resetOrder
  } = useSidebarVisibility();
  const userRole = authState.user?.role as UserRole;

  const [availableItems, setAvailableItems] = useState<MenuItemWithVisibility[]>([]);
  const [visibilityState, setVisibilityState] = useState<Record<string, boolean>>({});
  const [refreshKey, setRefreshKey] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [activeTab, setActiveTab] = useState("visibility");

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Получить все доступные пункты меню для текущего пользователя
  const availableItemsMemo = useMemo(() => {
    return items
      .filter((item) => {
        if (!item || !item.icon) return false;
        return (
          item.roles.includes(userRole) ||
          (item.children &&
            item.children.some((child) => child.roles.includes(userRole)))
        );
      })
      .map((item) => {
        const visible = isItemVisible(item.key);
        const children = item.children
          ? item.children
              .filter((child) => child.roles.includes(userRole))
              .map((child) => ({
                key: child.key,
                label: child.label,
                visible: isItemVisible(child.key),
                hasChildren: false,
                icon: child.icon,
              }))
          : undefined;

        return {
          key: item.key,
          label: item.label,
          visible,
          hasChildren: !!children && children.length > 0,
          icon: item.icon,
          children,
        };
      });
  }, [userRole, isItemVisible]);

  useEffect(() => {
    setAvailableItems(availableItemsMemo);
    const settings = getAllSettings();
    setVisibilityState(settings);
  }, [availableItemsMemo, getAllSettings, refreshKey]);

  // Обновить видимость пункта
  const handleVisibilityChange = (itemKey: string, visible: boolean) => {
    setItemVisibility(itemKey, visible);
    setVisibilityState((prev) => ({
      ...prev,
      [itemKey]: visible,
    }));
    setRefreshKey((prev) => prev + 1);
  };

  // Обновить видимость дочернего пункта
  const handleChildVisibilityChange = (parentKey: string, childKey: string, visible: boolean) => {
    setItemVisibility(childKey, visible);
    setVisibilityState((prev) => ({
      ...prev,
      [childKey]: visible,
    }));
    setRefreshKey((prev) => prev + 1);
  };

  // Показать все пункты
  const showAll = () => {
    const allKeys: Record<string, boolean> = {};
    const collectKeys = (items: MenuItemWithVisibility[]) => {
      items.forEach((item) => {
        allKeys[item.key] = true;
        if (item.children) {
          item.children.forEach((child) => {
            allKeys[child.key] = true;
          });
        }
      });
    };
    collectKeys(availableItems);
    setItemsVisibility(allKeys);
    setVisibilityState(allKeys);
    setRefreshKey((prev) => prev + 1);
    message.success("Все пункты меню отображаются");
  };

  // Скрыть все пункты
  const hideAll = () => {
    const allKeys: Record<string, boolean> = {};
    const collectKeys = (items: MenuItemWithVisibility[]) => {
      items.forEach((item) => {
        allKeys[item.key] = false;
        if (item.children) {
          item.children.forEach((child) => {
            allKeys[child.key] = false;
          });
        }
      });
    };
    collectKeys(availableItems);
    setItemsVisibility(allKeys);
    setVisibilityState(allKeys);
    setRefreshKey((prev) => prev + 1);
    message.success("Все пункты меню скрыты");
  };

  // Сбросить настройки (показать все по умолчанию)
  const resetToDefault = () => {
    resetVisibility();
    setVisibilityState({});
    setRefreshKey((prev) => prev + 1);
    message.success("Настройки сброшены. Все пункты отображаются по умолчанию");
  };

  const visibleCount = useMemo(() => {
    let count = 0;
    availableItems.forEach((item) => {
      if (visibilityState[item.key] !== false) count++;
      if (item.children) {
        item.children.forEach((child) => {
          if (visibilityState[child.key] !== false) count++;
        });
      }
    });
    return count;
  }, [availableItems, visibilityState]);

  const totalCount = useMemo(() => {
    let count = 0;
    availableItems.forEach((item) => {
      count++;
      if (item.children) {
        count += item.children.length;
      }
    });
    return count;
  }, [availableItems]);

  // Фильтрация по поисковому запросу
  const filteredItems = useMemo(() => {
    if (!searchText.trim()) return availableItems;
    
    const searchLower = searchText.toLowerCase();
    const result: MenuItemWithVisibility[] = [];
    
    availableItems.forEach((item) => {
      const matchesItem = item.label.toLowerCase().includes(searchLower);
      const filteredChildren = item.children?.filter((child) =>
        child.label.toLowerCase().includes(searchLower)
      );
      
      if (matchesItem || (filteredChildren && filteredChildren.length > 0)) {
        result.push({
          ...item,
          children: matchesItem ? item.children : filteredChildren,
        });
      }
    });
    
    return result;
  }, [availableItems, searchText]);

  const hiddenCount = totalCount - visibleCount;

  // Обработчик drag and drop для изменения порядка
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = availableItems.findIndex((item) => item.key === active.id);
      const newIndex = availableItems.findIndex((item) => item.key === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newItems = arrayMove(availableItems, oldIndex, newIndex);
        setAvailableItems(newItems);
        
        // Сохраняем новый порядок
        const newOrder = newItems.map((item) => item.key);
        setItemOrder(newOrder);
        message.success("Порядок пунктов меню обновлен");
      }
    }
  };

  // Компонент для сортируемого элемента
  const SortableItem: React.FC<{
    item: MenuItemWithVisibility;
    isVisible: boolean;
  }> = ({ item, isVisible }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: item.key });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    return (
      <div 
        ref={setNodeRef} 
        style={{
          ...style,
          marginBottom: 16,
          padding: "16px",
          backgroundColor: "#fff",
          borderRadius: "8px",
          border: "2px solid #e8e8e8",
          boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
          cursor: "grab",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
        {...attributes}
        {...listeners}
      >
        <DragOutlined style={{ fontSize: 18, color: "#8c8c8c" }} />
        <Space size="middle" style={{ flex: 1 }}>
          {item.icon && (
            <span style={{ 
              fontSize: 18, 
              display: "inline-flex", 
              alignItems: "center",
              color: isVisible ? "#52c41a" : "#8c8c8c"
            }}>
              {item.icon}
            </span>
          )}
          <Text strong style={{ fontSize: 16 }}>
            {item.label}
          </Text>
          {isVisible ? (
            <Tag color="success" icon={<EyeOutlined />}>
              Видно
            </Tag>
          ) : (
            <Tag color="error" icon={<EyeInvisibleOutlined />}>
              Скрыто
            </Tag>
          )}
        </Space>
      </div>
    );
  };

  return (
    <div style={{ padding: "24px", maxWidth: 900, margin: "0 auto" }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          {/* Заголовок и описание */}
          <div>
            <Space align="center" style={{ marginBottom: 12 }}>
              <SettingOutlined style={{ fontSize: 28, color: "#1890ff" }} />
              <Title level={3} style={{ margin: 0 }}>
                Настройки отображения меню
              </Title>
            </Space>
            <Alert
              message="Как это работает?"
              description={
                <div>
                  <Paragraph style={{ margin: "8px 0" }}>
                    <CheckCircleOutlined style={{ color: "#52c41a", marginRight: 8 }} />
                    Отметьте пункты, которые хотите <strong>видеть</strong> в боковом меню
                  </Paragraph>
                  <Paragraph style={{ margin: "8px 0" }}>
                    <CloseCircleOutlined style={{ color: "#ff4d4f", marginRight: 8 }} />
                    Снимите отметку, чтобы <strong>скрыть</strong> ненужные пункты
                  </Paragraph>
                  <Paragraph style={{ margin: "8px 0", marginBottom: 0 }}>
                    <InfoCircleOutlined style={{ color: "#1890ff", marginRight: 8 }} />
                    Изменения применяются <strong>мгновенно</strong> - просто посмотрите на сайдбар слева
                  </Paragraph>
                </div>
              }
              type="info"
              icon={<InfoCircleOutlined />}
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            {/* Статистика */}
            <div style={{ 
              display: "flex", 
              gap: 16, 
              flexWrap: "wrap",
              padding: "12px",
              backgroundColor: "#f5f5f5",
              borderRadius: "6px"
            }}>
              <Space>
                <Badge 
                  count={visibleCount} 
                  showZero 
                  style={{ backgroundColor: "#52c41a" }}
                >
                  <Tag color="success" icon={<EyeOutlined />}>
                    Видимых: <strong>{visibleCount}</strong>
                  </Tag>
                </Badge>
              </Space>
              <Space>
                <Badge 
                  count={hiddenCount} 
                  showZero 
                  style={{ backgroundColor: "#ff4d4f" }}
                >
                  <Tag color="error" icon={<EyeInvisibleOutlined />}>
                    Скрытых: <strong>{hiddenCount}</strong>
                  </Tag>
                </Badge>
              </Space>
              <Tag color="default">
                Всего: <strong>{totalCount}</strong> пунктов
              </Tag>
            </div>
          </div>

          <Divider />

          {/* Вкладки */}
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: "visibility",
                label: (
                  <Space>
                    <EyeOutlined />
                    Видимость
                  </Space>
                ),
              },
              {
                key: "order",
                label: (
                  <Space>
                    <UnorderedListOutlined />
                    Порядок
                  </Space>
                ),
              },
            ]}
          />

          {/* Поиск (только для вкладки видимости) */}
          {activeTab === "visibility" && (
            <Input
              placeholder="Поиск пунктов меню..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              size="large"
              style={{ marginBottom: 8 }}
            />
          )}

          {/* Кнопки управления */}
          <Space wrap style={{ width: "100%", justifyContent: "space-between" }}>
            <Space wrap>
              {activeTab === "visibility" ? (
                <>
                  <Tooltip title="Показать все пункты меню в сайдбаре">
                    <Button 
                      type="primary" 
                      icon={<EyeOutlined />} 
                      onClick={showAll}
                      size="large"
                    >
                      Показать все
                    </Button>
                  </Tooltip>
                  <Tooltip title="Скрыть все пункты меню">
                    <Button 
                      danger
                      icon={<EyeInvisibleOutlined />} 
                      onClick={hideAll}
                      size="large"
                    >
                      Скрыть все
                    </Button>
                  </Tooltip>
                  <Tooltip title="Вернуть настройки по умолчанию (показать все)">
                    <Button 
                      icon={<ReloadOutlined />} 
                      onClick={resetToDefault}
                      size="large"
                    >
                      Сбросить
                    </Button>
                  </Tooltip>
                </>
              ) : (
                <>
                  <Tooltip title="Вернуть порядок по умолчанию">
                    <Button 
                      icon={<ReloadOutlined />} 
                      onClick={() => {
                        resetOrder();
                        message.success("Порядок сброшен");
                        setRefreshKey((prev) => prev + 1);
                      }}
                      size="large"
                    >
                      Сбросить порядок
                    </Button>
                  </Tooltip>
                </>
              )}
            </Space>
            {activeTab === "visibility" && searchText && (
              <Text type="secondary">
                Найдено: <strong>{filteredItems.length}</strong> пунктов
              </Text>
            )}
          </Space>

          {/* Список пунктов меню */}
          <div style={{ 
            marginTop: 16, 
            maxHeight: "65vh", 
            overflowY: "auto",
            padding: "12px",
            border: "1px solid #d9d9d9",
            borderRadius: "8px",
            backgroundColor: "#fafafa"
          }}>
            {activeTab === "order" ? (
              // Вкладка изменения порядка с drag-and-drop
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={availableItems.map((item) => item.key)}
                  strategy={verticalListSortingStrategy}
                >
                  {availableItems.map((item) => {
                    const isVisible = visibilityState[item.key] !== false;
                    return (
                      <SortableItem 
                        key={item.key} 
                        item={item} 
                        isVisible={isVisible}
                      />
                    );
                  })}
                </SortableContext>
              </DndContext>
            ) : filteredItems.length === 0 ? (
              <Empty 
                description={
                  <Text type="secondary">
                    {searchText ? "Ничего не найдено" : "Нет доступных пунктов меню"}
                  </Text>
                }
                style={{ padding: "40px 0" }}
              />
            ) : (
              filteredItems.map((item) => {
                const isVisible = visibilityState[item.key] !== false;
                return (
                  <div 
                    key={item.key} 
                    style={{ 
                      marginBottom: 16,
                      padding: "16px",
                      backgroundColor: "#fff",
                      borderRadius: "8px",
                      border: isVisible ? "2px solid #52c41a" : "2px solid #ffccc7",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
                      transition: "all 0.3s"
                    }}
                  >
                    {/* Основной пункт */}
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <Checkbox
                        checked={isVisible}
                        onChange={(e) => handleVisibilityChange(item.key, e.target.checked)}
                        style={{ flex: 1 }}
                      >
                        <Space size="middle" style={{ width: "100%" }}>
                          {item.icon && (
                            <span style={{ 
                              fontSize: 18, 
                              display: "inline-flex", 
                              alignItems: "center",
                              color: isVisible ? "#52c41a" : "#8c8c8c"
                            }}>
                              {item.icon}
                            </span>
                          )}
                          <Text strong style={{ fontSize: 16 }}>
                            {item.label}
                          </Text>
                          {isVisible ? (
                            <Tag color="success" icon={<EyeOutlined />}>
                              Видно
                            </Tag>
                          ) : (
                            <Tag color="error" icon={<EyeInvisibleOutlined />}>
                              Скрыто
                            </Tag>
                          )}
                        </Space>
                      </Checkbox>
                    </div>
                    
                    {/* Дочерние пункты */}
                    {item.hasChildren && item.children && item.children.length > 0 && (
                      <div style={{ marginLeft: 40, marginTop: 16 }}>
                        <Divider style={{ margin: "12px 0" }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Подпункты ({item.children.length})
                          </Text>
                        </Divider>
                        <div style={{ 
                          display: "grid", 
                          gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
                          gap: 12
                        }}>
                          {item.children.map((child) => {
                            const childVisible = visibilityState[child.key] !== false;
                            return (
                              <div 
                                key={child.key} 
                                style={{ 
                                  padding: "12px",
                                  backgroundColor: childVisible ? "#f6ffed" : "#fff2f0",
                                  borderRadius: "6px",
                                  border: childVisible ? "1px solid #b7eb8f" : "1px solid #ffccc7",
                                  transition: "all 0.2s"
                                }}
                              >
                                <Checkbox
                                  checked={childVisible}
                                  onChange={(e) =>
                                    handleChildVisibilityChange(item.key, child.key, e.target.checked)
                                  }
                                >
                                  <Space size="small" style={{ width: "100%" }}>
                                    {child.icon && (
                                      <span style={{ 
                                        fontSize: 14, 
                                        display: "inline-flex", 
                                        alignItems: "center",
                                        color: childVisible ? "#52c41a" : "#8c8c8c"
                                      }}>
                                        {child.icon}
                                      </span>
                                    )}
                                    <Text style={{ fontSize: 14 }}>{child.label}</Text>
                                  </Space>
                                </Checkbox>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Подсказка внизу */}
          <Alert
            message="💡 Совет"
            description={
              activeTab === "order" 
                ? "Перетащите пункты меню для изменения их порядка в сайдбаре. Изменения применяются мгновенно."
                : "Используйте поиск для быстрого нахождения нужных пунктов меню. Изменения сохраняются автоматически и применяются сразу."
            }
            type="success"
            showIcon
            closable
            style={{ marginTop: 16 }}
          />
        </Space>
      </Card>
    </div>
  );
}

